/**
 * The general menu — **Escape**.
 *
 * Escape already meant three things: drop the armed verb, let go of whatever
 * was chosen or followed, and close whichever panel was open. All three are
 * *letting go*, and none of them is a menu, so the key now runs a precedence
 * chain and only opens this when there is nothing left to release:
 *
 *     a panel is open        → close it
 *     something is held      → let go of it (armed verb, chosen thing, followed life)
 *     otherwise              → open this
 *
 * That keeps the muscle memory the tip line has been teaching since v0.3 and
 * still gives Escape somewhere to go.
 *
 * **It does not pause the world.** `DESIGN.md` §1.1: *not a static sandbox
 * that waits for the player.* A menu that stopped time would be the one place
 * in the game where the world does wait, and the whole design is that it does
 * not. The clock keeps running behind it, which is also why the panel is
 * translucent rather than a full curtain — you can see it happening.
 *
 * Render-only and law-9 safe: opening, reading and closing this writes
 * nothing anywhere.
 */

export interface MenuActions {
  onSave(): void;
  onLoad(): void;
  /** Returns the new state, as `hud`'s sound button does. */
  onSound(): boolean;
  openGodhood(): void;
  openSettings(): void;
}

export interface Menu {
  readonly isOpen: boolean;
  open(): void;
  close(): void;
  toggle(): void;
}

/**
 * Every control, in one place, grouped by what it is *for* rather than by
 * which key it happens to sit on. This replaces the single run-on line that
 * used to sit along the bottom of the screen, which had grown to fourteen
 * clauses and was unreadable by about the fifth.
 */
const CONTROLS: Array<{ group: string; rows: Array<[string, string]> }> = [
  {
    group: 'being somewhere',
    rows: [
      ['drag', 'turn the eye about what it is looking at'],
      ['right-drag · W A S D', 'carry the eye across the land'],
      ['scroll', 'closer, further — and with it the scale of what you mean, one tree to a watershed'],
      ['space · C', 'look down onto it, or along it'],
      ['shift', 'surge'],
    ],
  },
  {
    group: 'attending to something',
    rows: [
      ['hover', 'name whatever is under the gaze'],
      ['click', 'choose it — the eye goes to it, its card opens, and if it '
        + 'moves you move with it'],
      ['right-drag · W A S D', 'go your own way; the eye lets go at once and the card stays'],
      ['esc', 'let go of what you are holding'],
    ],
  },
  {
    group: 'acting',
    rows: [
      ['palette', 'arm an Expression, then click — or hold the modifier below'],
      ['double-click', 'strike'],
      ['hold G', 'take hold of something and move it'],
      ['hold G', '…and on bare ground, take hold of the ground itself'],
      ['V', 'Anchor — you must come down and stand on the spot'],
      ['shift-V', 'leave part of yourself burning at one of your stones'],
      ['Y', 'hurry what the land is already doing'],
    ],
  },
  {
    // the mortal skeleton (2026-08-19): a body to see the world from, not yet
    // a body to do anything with — `ui/mortalPresence.ts`
    group: 'being someone',
    rows: [
      ['inhabit', 'choose a person, then the circle at the end of the bar — you stand where they stand'],
      ['drag', 'turn your head'],
      ['hover · click', 'name it · choose it — the card opens; nothing is cast, because you have no hands for that'],
      ['W A S D · shift', 'walk · run'],
      ['space', 'jump'],
      ['ascend', 'the same circle — your eye is where you left it'],
    ],
  },
  {
    group: 'the rest',
    rows: [
      ['tab', 'Become — what kind of god this is turning into'],
      ['esc', 'this, and everything in it'],
      ['K · L', 'remember · return'],
      ['—', 'there is one clock and it does not stop'],
    ],
  },
];

export function createMenu(actions: MenuActions): Menu {
  const style = document.createElement('style');
  /*
   * The sheet is meant to read as a leaf of a book that was made by hand and
   * happens to be open over the world — not as a dialog. Four things carry
   * that, and none of them is an image, because the app is offline and owns no
   * assets:
   *
   *   · the type does the work. A serif display line, a small-caps subtitle at
   *     wide tracking, and the whole control list in the same mono the rest of
   *     the chrome uses, so the page has two voices and not five.
   *   · the rules fade at their ends and carry a lozenge where the hand
   *     lifted (`.rule` in index.html). A full-width 1px line is printing; a
   *     rule that stops short of the margin is drawing.
   *   · the actions are words with a hairline under them rather than buttons
   *     with boxes around them. Six boxed pills at the top of a page is a
   *     settings screen, and this is not one.
   *   · it arrives. 340 ms on the one easing every other panel uses, opacity
   *     and a small rise, so opening the menu feels like a page being turned
   *     toward you rather than a layer being switched on.
   *
   * The backdrop stays translucent and lightly blurred on purpose: `D §1.1`,
   * the world does not wait, and you have to be able to SEE that it does not.
   */
  style.textContent = `
    #menu { position:fixed; inset:0; z-index:70; display:none;
      background:rgba(10,11,9,.58); backdrop-filter:blur(5px);
      -webkit-backdrop-filter:blur(5px); }
    #menu.open { display:flex; align-items:center; justify-content:center; }
    #menu .sheet { width:min(720px,92vw); max-height:88vh; overflow-y:auto;
      padding:38px 42px 32px; background:var(--pane); border:1px solid var(--edge);
      border-radius:3px; box-shadow:var(--lift), inset 0 1px 0 rgba(233,227,210,.06);
      position:relative;
      opacity:0; transform:translateY(9px);
      transition:opacity .34s var(--ease), transform .34s var(--ease); }
    #menu.open .sheet { opacity:1; transform:none; }
    /* the illuminated corner, once, at the head of the page */
    #menu .sheet::before { content:''; position:absolute; top:11px; left:11px;
      width:16px; height:16px; border:1px solid var(--gold-deep); border-right:0;
      border-bottom:0; opacity:.5; }
    #menu .sheet::after { content:''; position:absolute; bottom:11px; right:11px;
      width:16px; height:16px; border:1px solid var(--gold-deep); border-left:0;
      border-top:0; opacity:.5; }
    #menu h2 { font:italic 400 34px/1 var(--serif); color:var(--ink);
      letter-spacing:.05em; }
    #menu .sub { margin-top:9px; font-size:9px; letter-spacing:.3em;
      text-transform:uppercase; color:var(--dim); }
    /* the actions: words, underlined by a hairline, not pills */
    #menu .acts { display:flex; flex-wrap:wrap; gap:4px 22px; margin:24px 0 6px; }
    #menu .acts button { padding:4px 1px 5px; font-size:10px; letter-spacing:.2em;
      text-transform:uppercase; color:var(--dim);
      border-bottom:1px solid transparent; transition:color .18s, border-color .18s; }
    #menu .acts button:hover { color:var(--gold); border-bottom-color:var(--gold); }
    #menu .acts button.go { color:var(--ink); border-bottom-color:var(--line); }
    #menu .acts button.off { opacity:.45; }
    #menu .rule { margin:22px 0; }
    #menu .cols { display:grid; grid-template-columns:1fr 1fr; gap:24px 40px;
      margin-top:6px; }
    #menu h3 { font:italic 400 15px/1 var(--serif); letter-spacing:.03em;
      color:var(--gold); margin-bottom:9px; font-weight:400; }
    #menu table { width:100%; border-collapse:collapse; }
    #menu td { font-size:10px; color:var(--dim); padding:2px 0; vertical-align:top;
      line-height:1.6; }
    #menu td.k { color:var(--ink); white-space:nowrap; padding-right:14px; width:1%;
      letter-spacing:.06em; }
    #menu footer { margin-top:26px; font:italic 400 13px/1.75 var(--serif);
      color:var(--dim); }
    #menu footer em { color:var(--gold); font-style:italic; }
    @media (max-width: 720px) { #menu .cols { grid-template-columns:1fr; } }
    @media (prefers-reduced-motion: reduce) { #menu .sheet { transition-duration:.01ms; } }
  `;
  document.head.appendChild(style);

  const el = document.createElement('div');
  el.id = 'menu';
  el.innerHTML = `
    <div class="sheet">
      <h2>Unseen</h2>
      <div class="sub">the world does not wait — this is still happening</div>
      <div class="acts">
        <button class="go" data-a="resume">resume</button>
        <button data-a="godhood">become</button>
        <button data-a="settings">settings</button>
        <button data-a="save">remember</button>
        <button data-a="load">return</button>
        <button data-a="sound">sound</button>
      </div>
      <div class="rule"></div>
      <div class="cols">${CONTROLS.map((g) => `
        <div>
          <h3>${g.group}</h3>
          <table>${g.rows.map(([k, what]) =>
            `<tr><td class="k">${k}</td><td>${what}</td></tr>`).join('')}</table>
        </div>`).join('')}
      </div>
      <div class="rule"></div>
      <footer>
        You act simply; something that loves you interprets it, and the
        interpretation escapes you. Nothing you do here is explained to anyone
        in the world — a divine act reaches every eye as <em>unexplained</em>,
        and that gap is where religion comes from.
      </footer>
    </div>`;
  document.body.appendChild(el);

  const setOpen = (open: boolean) => {
    el.classList.toggle('open', open);
  };

  const soundBtn = el.querySelector<HTMLButtonElement>('[data-a="sound"]')!;
  const hit: Record<string, () => void> = {
    resume: () => setOpen(false),
    godhood: () => { setOpen(false); actions.openGodhood(); },
    settings: () => { setOpen(false); actions.openSettings(); },
    save: () => { actions.onSave(); setOpen(false); },
    load: () => { actions.onLoad(); setOpen(false); },
    sound: () => soundBtn.classList.toggle('off', !actions.onSound()),
  };
  for (const b of el.querySelectorAll<HTMLButtonElement>('[data-a]')) {
    b.addEventListener('click', () => hit[b.dataset.a!]?.());
  }
  // a click on the backdrop is a resume, which is what everyone expects
  el.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    if (e.target === el) setOpen(false);
  });

  // Deliberately NO Escape handler here. main.ts owns that key and runs the
  // whole precedence chain, because two independent listeners raced: this one
  // closed the menu, main's then saw nothing open and reopened it in the same
  // keypress. One key, one owner.

  return {
    get isOpen() { return el.classList.contains('open'); },
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!el.classList.contains('open')),
  };
}
